/**
 * @file FieldChoice.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-16
 * @modified 2026-10-28
 * @description A field that can take the PO's value or the bill's — offered
 *              in the field itself (Cam, 16 Oct 2026: "just clicking a field
 *              can offer PO's value, Bill's value, or choose / input
 *              another"). Add / Edit Bill's header fields and line cells.
 *
 *              The field works as it always did — type in it, pick in it.
 *              Where the PO and the bill disagree, a small marker in its
 *              corner says which is in use (PO / Bill / Own); focusing the
 *              field (a text or number box) or clicking the marker (a picker)
 *              opens a small card:
 *
 *                PO    80 × 25kg bag          ✓ in use
 *                Bill  2,000.00 kg
 *                Type another
 *                ─────────────────────────
 *                (extra: "Also update the PO to this", the units maths …)
 *
 *              Where they agree it shows plainly: no marker, no card. Never a
 *              gate: picking a value is a shortcut for typing it.
 *
 *              The card is portalled to <body> (a table cell clips what
 *              overflows it) and placed under the field; it closes on a pick,
 *              on Escape, and when the pointer or focus leaves both.
 *
 *              1.1.0 (2026-10-28, Cam — a bill line's cost that differs from
 *              the order's or the contract's must be chosen): `required` — no
 *              value chosen yet: the field is outlined in the error colour and
 *              its marker reads `markerText` ("Choose"), never "Own".
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './FieldChoice.css';

/** The marker's word for the value in use. */
const tagOf = (choices, active) => {
  const c = (choices || []).find(x => x.key === active);
  return c ? c.tag : 'Own';
};

/**
 * @param {object} props
 * @param {Array<{ key: string, tag: string, text: string, onPick: () => void, disabled?: boolean, title?: string, hint?: string }>} props.choices
 * @param {string|null} [props.active] — the key in use (null: something else was typed / chosen)
 * @param {boolean} [props.differs] — the values disagree: show the marker
 * @param {string} [props.label] — what the field is, for the card's heading and aria
 * @param {{ label: string, onPick?: () => void }|null} [props.other] — "Type another" / "Choose another…"
 * @param {React.ReactNode} [props.extra] — under the choices (a tick, a line of maths)
 * @param {boolean} [props.openOnFocus] — a text / number box: the card opens as it is focused
 * @param {boolean} [props.forceMarker] — show the marker even where they agree (something in `extra`)
 * @param {boolean} [props.required] — nothing chosen yet, and it must be (outlined, the marker says so)
 * @param {string} [props.markerText] — the marker's word while `required` (default "Choose")
 * @param {string} [props.className]
 */
export default function FieldChoice({
  choices = [], active = null, differs = false, label = '', other = null, extra = null,
  openOnFocus = true, forceMarker = false, required = false, markerText = 'Choose', className = '', children,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const cardRef = useRef(null);
  const showMarker = differs || forceMarker || required;

  const place = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(240, Math.min(360, r.width + 120));
    const left = Math.min(Math.max(8, r.left), Math.max(8, window.innerWidth - width - 8));
    const below = r.bottom + 4;
    const tooLow = below + 220 > window.innerHeight && r.top > 240;
    setPos({ left, width, ...(tooLow ? { bottom: window.innerHeight - r.top + 4 } : { top: below }) });
  }, []);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (wrapRef.current?.contains(e.target) || cardRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const key = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away, true);
    document.addEventListener('keydown', key, true);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', away, true);
      document.removeEventListener('keydown', key, true);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  if (!showMarker) return <div className={`field-choice ${className}`.trim()}>{children}</div>;

  const pick = (c) => {
    if (c.disabled) return;
    c.onPick?.();
    setOpen(false);
  };
  const doOther = () => {
    setOpen(false);
    if (other?.onPick) { other.onPick(); return; }
    // Type another: into the field's own box; choose another: open its picker.
    const box = wrapRef.current?.querySelector('input:not([type="checkbox"]), textarea');
    if (box) { box.focus(); box.select?.(); return; }
    const trigger = wrapRef.current?.querySelector('button:not(.field-choice-marker)');
    if (trigger) { trigger.focus(); trigger.click(); }
  };

  const card = open && pos ? ReactDOM.createPortal(
    <div
      ref={cardRef}
      className="field-choice-card"
      role="dialog"
      aria-label={label ? `${label}: the PO's or the bill's` : "The PO's or the bill's"}
      style={{ left: pos.left, width: pos.width, ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom }) }}
      // Keep the focus in the field while the card is used.
      onMouseDown={(e) => { if (!e.target.closest('input, textarea, select')) e.preventDefault(); }}
    >
      {label && <div className="field-choice-head">{label}</div>}
      {choices.map(c => (
        <button
          key={c.key}
          type="button"
          className={`field-choice-option${c.key === active ? ' is-active' : ''}`}
          disabled={!!c.disabled}
          title={c.title || undefined}
          onClick={() => pick(c)}
        >
          <span className={`field-choice-tag tag-${c.key}`}>{c.tag}</span>
          <span className="field-choice-text">
            {c.text}
            {c.hint && <small>{c.hint}</small>}
          </span>
          {c.key === active && <span className="material-symbols-rounded field-choice-tick" aria-label="in use">check</span>}
        </button>
      ))}
      {other && (
        <button type="button" className={`field-choice-option field-choice-other${active === null && !required ? ' is-active' : ''}`} onClick={doOther}>
          <span className="field-choice-tag tag-own">{active === null && !required ? 'Own' : '…'}</span>
          <span className="field-choice-text">{other.label}</span>
          {active === null && !required && <span className="material-symbols-rounded field-choice-tick" aria-label="in use">check</span>}
        </button>
      )}
      {extra && <div className="field-choice-extra">{extra}</div>}
    </div>,
    document.body
  ) : null;

  return (
    <div
      ref={wrapRef}
      className={`field-choice has-choice${differs ? ' is-different' : ''}${required ? ' is-required' : ''} ${className}`.trim()}
      onFocus={openOnFocus ? (e) => { if (!e.target.closest('.field-choice-marker')) setOpen(true); } : undefined}
      // Typing is choosing your own: the card gets out of the way.
      onKeyDown={(e) => { if (open && (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Tab')) setOpen(false); }}
    >
      {children}
      <button
        type="button"
        className={`field-choice-marker tag-${required ? 'required' : (active || 'own')}`}
        tabIndex={-1}
        title={required ? 'Nothing chosen yet — click to choose' : (differs ? 'The PO and the bill differ here — click to choose' : 'Click for the PO\'s and the bill\'s')}
        aria-label={required ? `${label || 'Field'}: nothing chosen — choose` : `${label || 'Field'}: ${tagOf(choices, active)} in use — choose`}
        aria-expanded={open}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen(o => !o)}
      >
        {required ? markerText : tagOf(choices, active)}
      </button>
      {card}
    </div>
  );
}
